import { Dialog, DialogActions, DialogContent, IconButton, TextField } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { Close } from "@mui/icons-material";
import React, { useEffect, useMemo, useRef, useState } from "react";
import "moment-timezone";
import MButton from "components/momentum/MButton";
import FileUploader, { FileUploaderRef } from "components/app/FileUploader";
import { QVR } from "types/summary";
import { useForm, Controller } from "react-hook-form";
import QVRFiscalQuarter from "components/summary/index/QVR/QVRDialogFiscalQuarter";
import QVRAudience from "components/summary/index/QVR/QVRDialogAudience";
import QVRPainPoints from "components/summary/index/QVR/QVRDialogPainPoints";
import QVRDialogRole from "components/summary/index/QVR/QVRDialogRole";
import QVRDialogNextDate from "components/summary/index/QVR/QVRDialogNextDate";
import { toast } from "react-toastify";
import moment from "moment";
import axios from "axios";
import { useAccountById } from "components/summary/hooks";

const useStyles = makeStyles(() => ({
  titleContainer: {
    display: "flex",
    justifyContent: "space-between",
    padding: "16px 24px",
    alignItems: "center",
  },
  title: {
    fontSize: 16,
    color: "#171B1F",
    fontWeight: 500,
    display: "flex",
    lineHeight: "24px",
  },
  field: {
    marginTop: 24,
  },
  fieldLabel: {
    marginBottom: 8,
    fontSize: 12,
    color: "#535759",
    fontWeight: 500,
  },
  textField: {
    width: 200,
  },
  errorFiled: {
    border: "1px solid #f44336",
    marginBottom: 8,
    borderRadius: "8px",
  },
}));

export interface QVRDialogProps {
  accountId?: string;
  accountKey?: string;
  open: boolean;
  onClose: () => void;
  type: "Add New" | "Update";
  mutate?: () => Promise<void>;
  data?: QVR;
}

interface RequestParams {
  id?: string | undefined;
  accountKey: string | undefined;
  fiscalQuarter: string;
  audienceIds: string;
  qvrRole: string;
  painPoints: string[];
  actionItem: string;
  otherNote: string;
  attachmentIds: string[] | null;
  nextScheduledDate: string | undefined;
}

const QVRDialog: React.FC<QVRDialogProps> = ({
  accountId,
  accountKey,
  open,
  onClose,
  type,
  data,
  mutate,
}) => {
  const fileUploaderRef = useRef<FileUploaderRef>(null);

  const classes = useStyles();

  const { handleSubmit, control, reset, clearErrors, setValue } = useForm();

  useEffect(() => {
    if (!open) {
      clearErrors(["fiscal", "role", "audience", "painPoints", "actionItem"]);
      if (type === "Update") {
        reset();
      }
    }
  }, [open, type]);

  useEffect(() => {
    if (data) {
      setValue("actionItem", data.actionItem);
      setValue("otherNote", data.otherNote);
      setValue("nextScheduledDate", data.nextScheduledDate);
    }
  }, [data]);

  const { data: account } = useAccountById(open ? accountId : undefined);
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (submitData: { [key: string]: string & string[] }) => {
    try {
      const { fiscal, audience, role, painPoints, actionItem, otherNote, nextScheduledDate } =
        submitData;
      const nextScheduledDateString = nextScheduledDate
        ? moment(nextScheduledDate)?.format("YYYY-MM-DD")
        : undefined;
      painPoints.forEach((item: any, idx) => {
        if (item && item.value) painPoints[idx] = item.value;
      });
      const params: RequestParams = {
        id: data ? data.id : undefined,
        accountKey: data ? data.accountKey : accountKey,
        fiscalQuarter: fiscal,
        audienceIds: audience,
        qvrRole: role,
        painPoints: painPoints,
        actionItem,
        otherNote,
        attachmentIds: null,
        nextScheduledDate: nextScheduledDateString,
      };
      setSubmitting(true);
      await request(params);
      setSubmitting(false);
      onClose();
    } catch (error) {
      toast.error("Business review modified fail, but fail to upload files", {
        position: "top-center",
        autoClose: 2000,
      });
      setSubmitting(false);
    }
  };
  const sendRequest = async (type: "Update" | "Add New", params: RequestParams) => {
    if (type === "Update") {
      try {
        await axios.put<string>(
          `/api/dataservice/v1/accounts/${data?.accountKey}/qvrs/${data?.id}`,
          params
        );
        mutate?.();
        toast.success("Business review modified successfully", {
          position: "top-center",
          autoClose: 2000,
        });
      } catch (e) {
        toast.error(e?.response?.data?.message || "Business review modified fail", {
          position: "top-center",
          autoClose: 2000,
        });
      }
    } else {
      try {
        await axios.post<string>(`/api/dataservice/v1/accounts/${params?.accountKey}/qvrs`, params);
        await mutate?.();
        toast.success("Business review added successfully", {
          position: "top-center",
          autoClose: 2000,
        });
      } catch (e) {
        toast.error(e?.response?.data?.message || "Business review added fail", {
          position: "top-center",
          autoClose: 2000,
        });
      }
    }
    reset();
  };
  const request = async (params: RequestParams) => {
    try {
      const attachmentIds = await fileUploaderRef.current?.submit({});
      if (attachmentIds) params.attachmentIds = attachmentIds;
      const currentFiles = fileUploaderRef.current?.getUploadedFiles();
      const deletedFiles =
        data?.attachments.filter((x) => !currentFiles?.find((y) => y.fileId === x.fileId)) || [];
      if (deletedFiles.length) {
        await Promise.all(
          deletedFiles.map(async (x) => axios.delete(`/api/dataservice/v1/files/${x.fileId}`))
        );
      }

      try {
        await sendRequest(type, params);
      } catch (e) {
        toast.error(e?.response?.data?.message || "Failed", {
          position: "top-center",
          autoClose: 2000,
        });
        throw e;
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || "Submit file failed", {
        position: "top-center",
        autoClose: 2000,
      });
      throw e;
    }
  };

  const onError = (errors: { [key: string]: any }) => {
    console.log(errors);
    if (Object.keys(errors).length) {
      toast.error("Please correct the highlighted field(s)", {
        position: "top-center",
        autoClose: 3000,
      });
    }
  };

  const fileUploaderMemo = useMemo(
    () => (
      <FileUploader
        ref={fileUploaderRef}
        submitURL={"/api/dataservice-multipart/v1/files"}
        note={{ files: data?.attachments, createdBy: data?.createdBy }}
        dryrun={true}
        batch={true}
      />
    ),
    [data]
  );

  return (
    <Dialog
      open={open}
      scroll={"body"}
      fullWidth={true}
      maxWidth={"md"}
      onClose={onClose}
    >
      <form
        style={{
          height: "100%",
          pointerEvents: submitting ? "none" : "inherit",
          filter: submitting ? "opacity(0.5)" : undefined,
        }}
      >
        <div className={classes.titleContainer}>
          <div>
            <p className={classes.title}>Business Review for {account?.accountName}</p>
          </div>
          <IconButton
            onClick={onClose}
            size="small"
          >
            <Close style={{ color: "#121212", fillOpacity: 0.6 }} />
          </IconButton>
        </div>
        <DialogContent
          dividers={true}
          style={{
            paddingTop: 0,
          }}
        >
          <div className={classes.field}>
            <div className={classes.fieldLabel}>
              <span style={{ color: "red" }}>* </span>Fiscal Year and Quarter:
            </div>
            <Controller
              rules={{
                required: { message: "This field is required!", value: true },
              }}
              control={control}
              name={"fiscal"}
              defaultValue={data ? data.fiscalQuarter : null}
              render={({ field, formState }) => (
                <QVRFiscalQuarter
                  {...field}
                  {...formState}
                />
              )}
            />
          </div>
          <div className={classes.field}>
            <div className={classes.fieldLabel}>
              <span style={{ color: "red" }}>* </span>Business Review Meeting:
            </div>
            <Controller
              control={control}
              name={"role"}
              rules={{
                required: { message: "This field is required!", value: true },
              }}
              defaultValue={data ? data.qvrRole : undefined}
              render={({ field, formState }) => (
                <QVRDialogRole
                  {...field}
                  {...formState}
                />
              )}
            />
          </div>
          <div className={classes.field}>
            <div className={classes.fieldLabel}>
              <span style={{ color: "red" }}>* </span>Audience:
            </div>
            <Controller
              control={control}
              rules={{
                required: { message: "This field is required!", value: true },
              }}
              name={"audience"}
              defaultValue={
                data?.audiences?.length ? data?.audiences?.map((x: { id: any }) => x.id) : undefined
              }
              render={({ field, formState }) => (
                <QVRAudience
                  accountId={accountId}
                  {...field}
                  {...formState}
                />
              )}
            />
          </div>
          <div className={classes.field}>
            <div className={classes.fieldLabel}>
              <span style={{ color: "red" }}>* </span>Customer Feedback:
            </div>
            <Controller
              control={control}
              rules={{
                required: { message: "This field is required!", value: true },
              }}
              name={"painPoints"}
              defaultValue={data ? data.painPoints : undefined}
              render={({ field, formState }) => (
                <QVRPainPoints
                  {...field}
                  {...formState}
                  clearErrors={clearErrors}
                />
              )}
            />
          </div>

          <div className={classes.field}>
            <div className={classes.fieldLabel}>
              <span style={{ color: "red" }}>* </span>Action Items:
            </div>
            <Controller
              control={control}
              rules={{
                required: {
                  message: "This field is required!",
                  value: true,
                },
              }}
              name={"actionItem"}
              defaultValue={data ? data.actionItem : undefined}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  onChange={(e) => {
                    field.onChange(e.currentTarget.value);
                  }}
                  multiline={true}
                  rows={4}
                  variant="outlined"
                  fullWidth={true}
                  placeholder="Please enter Action Items Impact/Outcomes"
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                />
              )}
            />
          </div>
          <div className={classes.field}>
            <div className={classes.fieldLabel}>Other Notes around Impact/Outcomes:</div>
            <Controller
              control={control}
              name={"otherNote"}
              defaultValue={data ? data.otherNote : undefined}
              render={({ field }) => (
                <TextField
                  {...field}
                  onChange={(e) => field.onChange(e.currentTarget.value)}
                  multiline={true}
                  fullWidth={true}
                  rows={4}
                  variant="outlined"
                  placeholder="Please enter Other Notes around Impact/Outcomes"
                />
              )}
            />
          </div>
          <div className={classes.field}>
            <div className={classes.fieldLabel}>Next Review Date:</div>
            <Controller
              control={control}
              name={"nextScheduledDate"}
              defaultValue={data ? data.nextScheduledDate : undefined}
              render={({ field }) => <QVRDialogNextDate {...field} />}
            />
          </div>
          <div className={classes.field}>{fileUploaderMemo}</div>
        </DialogContent>
        <DialogActions>
          <MButton
            type="submit"
            buttonType="blue"
            buttonSize={36}
            onClick={handleSubmit(onSubmit, onError)}
          >
            Submit
          </MButton>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default QVRDialog;
